/**
 * Zip helper — wraps the system `zip` command for inspiration uploads.
 *
 * The Inspiration API contract (`docs/contracts/inspiration-api.md` §4.1)
 * accepts `.zip` for `source_type: html` provided the archive contains
 * `index.html` at the root. Building a zip in pure JS would require a new
 * runtime dependency; mirroring the tarball helper's pattern of shelling out
 * keeps the dep set unchanged on the v0.8 timeline.
 */

import { execFileSync } from 'node:child_process';
import { existsSync, mkdtempSync, rmSync } from 'node:fs';
import { tmpdir } from 'node:os';
import { join } from 'node:path';

import { CloudGridError } from '../utils.js';

const ZIP_MAX_BUFFER = 200 * 1024 * 1024;

/**
 * Zip the contents of `sourceDir` into a temporary file. Returns
 * `{ path, cleanup }` — the caller invokes `cleanup()` after upload to
 * remove both the archive and its enclosing temp directory.
 *
 * Equivalent to `cd <sourceDir> && zip -r <archive>.zip .` so the resulting
 * archive's root is the directory's contents (not the directory itself).
 */
export function zipDirectory(sourceDir: string): { path: string; cleanup: () => void } {
  if (!existsSync(sourceDir)) {
    throw new CloudGridError(`Directory not found: ${sourceDir}`);
  }

  const outDir = mkdtempSync(join(tmpdir(), `cg-zip-${process.pid}-`));
  const outPath = join(outDir, 'archive.zip');
  const cleanup = () => rmSync(outDir, { recursive: true, force: true });

  try {
    execFileSync('zip', ['-r', '-q', outPath, '.'], {
      cwd: sourceDir,
      maxBuffer: ZIP_MAX_BUFFER,
      stdio: ['ignore', 'ignore', 'pipe'],
    });
  } catch (err) {
    cleanup();
    const orig = err instanceof Error ? err.message : String(err);
    throw new CloudGridError(
      `Failed to zip ${sourceDir}: ${orig}\n` +
        'The CLI uses the system `zip` command. Install it (e.g., `brew install zip` on macOS, `apt install zip` on Debian) and try again.',
    );
  }

  return { path: outPath, cleanup };
}
